import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import DragDropList from '../shared/drag_drop_list';
import QuestionBuilder from './question_builder';

const QuestionnaireBuilder = ({ 
  initialQuestionnaire = null,
  isEdit = false,
  importableFields = {}
}) => {
  
  const [questionnaire, setQuestionnaire] = useState(
    initialQuestionnaire || { name: '', description: '', questions: [] }
  );
  const [questions, setQuestions] = useState(
    initialQuestionnaire?.questions || []
  );
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    // Update position numbers when questions change
    const updatedQuestions = questions.map((question, index) => ({
      ...question,
      position: index + 1
    }));
    setQuestions(updatedQuestions);
  }, [questions.length]);

  useEffect(() => {
    setHasUnsavedChanges(true);
  }, [questionnaire, questions]);

  // Temporarily disabled to avoid annoying dialogs during development
  // useEffect(() => {
  //   const handleBeforeUnload = (e) => {
  //     if (hasUnsavedChanges) {
  //       e.preventDefault();
  //       e.returnValue = '';
  //     }
  //   };

  //   window.addEventListener('beforeunload', handleBeforeUnload);
  //   return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  // }, [hasUnsavedChanges]);

  // Handle when initialQuestionnaire prop changes (for edit mode)
  useEffect(() => {
    if (initialQuestionnaire && initialQuestionnaire.id) {
      setQuestionnaire(initialQuestionnaire);
      setQuestions(initialQuestionnaire.questions || []);
      setHasUnsavedChanges(false); // Don't mark as dirty when loading existing data
    }
  }, [initialQuestionnaire]);

  const handleQuestionnaireChange = (field, value) => {
    setQuestionnaire(prev => ({ ...prev, [field]: value }));
  };

  const handleQuestionChange = (index, updatedQuestion) => {
    const newQuestions = [...questions];
    newQuestions[index] = updatedQuestion;
    setQuestions(newQuestions);
  };

  const handleQuestionDelete = (index) => {
    const newQuestions = questions.filter((_, i) => i !== index);
    setQuestions(newQuestions);
  };

  const handleQuestionsReorder = (reorderedQuestions) => {
    setQuestions(reorderedQuestions);
  };

  const addQuestion = (type = 'text') => {
    const newQuestion = {
      id: null, // New question
      question_text: '',
      question_type: type,
      required: false,
      position: questions.length + 1,
      options: type === 'radio' || type === 'checkbox' || type === 'dropdown' ? [''] : [],
      has_other_option: false,
      other_option_label: '',
      conditional_question_id: null,
      conditional_answer: '',
      conditional_operator: 'equals'
    };
    
    setQuestions([...questions, newQuestion]);
  };

  const getAvailableConditionalQuestions = (currentIndex) => {
    // Only questions that appear before the current question can be used as conditions
    return questions.slice(0, currentIndex).map(q => ({
      id: q.id || `temp_${currentIndex}_${q.position}`,
      question_text: q.question_text || `Question ${q.position}`,
      question_type: q.question_type,
      options: q.options || []
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!questionnaire.name?.trim()) {
      newErrors.name = 'Questionnaire name is required';
    }
    
    questions.forEach((question, index) => {
      const questionErrors = {};
      
      if (!question.question_text?.trim()) {
        questionErrors.question_text = 'Question text is required';
      }
      
      if (['radio', 'checkbox', 'dropdown'].includes(question.question_type)) {
        // Handle both array and JSON string formats
        let options = question.options;
        if (typeof options === 'string') {
          try {
            options = JSON.parse(options);
          } catch (e) {
            options = [];
          }
        }
        
        if (!Array.isArray(options) || options.length === 0 || 
            options.every(opt => !opt.trim())) {
          questionErrors.options = 'At least one option is required';
        }
      }
      
      if (Object.keys(questionErrors).length > 0) {
        newErrors[`question_${index}`] = questionErrors;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (saveType = 'save') => {
    if (!validateForm()) {
      // Scroll to first error
      const firstError = document.querySelector('.is-invalid, .text-danger');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setSaving(true);
    setErrors({});

    try {
      const formData = new FormData();
      
      // Add questionnaire data
      formData.append('questionnaire[name]', questionnaire.name);
      formData.append('questionnaire[description]', questionnaire.description || '');
      
      // Add questions data as JSON
      const questionsData = questions.map((question, index) => ({
        ...question,
        position: index + 1,
        conditional_question_position: question.conditional_question_id ? 
          questions.find(q => (q.id || `temp_${index}_${q.position}`) === question.conditional_question_id)?.position :
          null
      }));
      
      formData.append('questions', JSON.stringify(questionsData));
      
      // Add CSRF token
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      if (csrfToken) {
        formData.append('authenticity_token', csrfToken);
      }

      const url = isEdit ? `/questionnaires/${questionnaire.id}` : '/questionnaires';
      const method = isEdit ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setHasUnsavedChanges(false);
          
          if (saveType === 'save_and_continue') {
            window.location.href = '/questionnaires';
          } else {
            // Redirect to the questionnaire show page after successful creation/update
            window.location.href = `/questionnaires/${data.questionnaire.id}`;
          }
        } else {
          setErrors(data.errors || {});
        }
      } else {
        const errorData = await response.json();
        setErrors(errorData.errors || {});
      }
    } catch (error) {
      console.error('Error saving questionnaire:', error);
      alert('An error occurred while saving. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges && !confirm('Are you sure? You have unsaved changes that will be lost.')) {
      return;
    }
    window.location.href = '/questionnaires';
  };

  const renderQuestion = (question, index) => (
    <QuestionBuilder
      key={question.id || `new_${index}`}
      question={question}
      index={index}
      onChange={(updatedQuestion) => handleQuestionChange(index, updatedQuestion)}
      onDelete={() => handleQuestionDelete(index)}
      availableConditionalQuestions={getAvailableConditionalQuestions(index)}
      errors={errors[`question_${index}`] || {}}
      importableFields={importableFields}
    />
  );

  return (
    <div className="questionnaire-builder">
      <div className="card">
        <div className="card-header bg-onboarding text-white d-flex">
          <h5 className="mb-0">
            <i className="fa fa-question-circle mr-2"></i>
            {isEdit ? 'Edit Questionnaire' : 'Create New Questionnaire'}
          </h5>
        </div>
        <div className="card-body">
          {Object.keys(errors).length > 0 && (
            <div className="alert alert-danger alert-dismissible mb-4" role="alert">
              <h6 className="alert-heading mb-2">
                <i className="fa fa-exclamation-triangle mr-2"></i>
                Please fix the following errors:
              </h6>
              <ul className="mb-0 pl-3">
                {Object.entries(errors).map(([key, error]) => {
                  if (key === 'name') {
                    return <li key={key}>Questionnaire name: {error}</li>;
                  } else if (key.startsWith('question_')) {
                    const questionIndex = parseInt(key.replace('question_', '')) + 1;
                    return Object.entries(error).map(([field, message]) => (
                      <li key={`${key}_${field}`}>
                        Question {questionIndex} ({field}): {message}
                      </li>
                    ));
                  }
                  return <li key={key}>{error}</li>;
                })}
              </ul>
            </div>
          )}
          <div className="row">
            <div className="col-md-8">
              <div className="form-group">
                <label className="form-label">Questionnaire Name</label>
                <input
                  type="text"
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  value={questionnaire.name}
                  onChange={(e) => handleQuestionnaireChange('name', e.target.value)}
                  placeholder="Enter questionnaire name..."
                />
                {errors.name && (
                  <div className="invalid-feedback">{errors.name}</div>
                )}
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-group">
                <label className="form-label">Questions</label>
                <div className="form-control-plaintext">
                  <span className="badge badge-secondary">{questions.length} questions</span>
                </div>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              rows="2"
              value={questionnaire.description}
              onChange={(e) => handleQuestionnaireChange('description', e.target.value)}
              placeholder="Optional description of this questionnaire..."
            />
          </div>

          <hr />

          <div className="mb-3">
            <h6 className="mb-0">Questions</h6>
          </div>

          {questions.length > 0 ? (
            <>
              <DragDropList
                items={questions}
                onReorder={handleQuestionsReorder}
                renderItem={renderQuestion}
                className="questions-list"
              />
              
              {/* Add Question button below the questions */}
              <div className="text-center mt-3">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => addQuestion('text')}
                >
                  <i className="fa fa-plus"></i> Add Question
                </button>
              </div>
            </>
          ) : (
            <div className="empty-state text-center py-4">
              <i className="fa fa-clipboard-list fa-3x text-muted mb-3"></i>
              <h6 className="text-muted">No questions yet</h6>
              <p className="text-muted">Add your first question to get started.</p>
              
              {/* Button for adding first question */}
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => addQuestion('text')}
              >
                <i className="fa fa-plus"></i> Add First Question
              </button>
            </div>
          )}
        </div>

        <div className="card-body">
          <div className="d-flex">
            <div className="ml-auto">
              <button
                type="button"
                className="btn btn-primary mr-2"
                onClick={() => handleSave('save')}
                disabled={saving || questions.length === 0}
              >
                {saving ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i> Saving...
                  </>
                ) : (
                  <>
                    <i className="fa fa-save"></i> Save
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn btn-outline-default"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .questionnaire-builder {
          /* Full width like other forms */
        }
        
        .questions-list {
          margin-bottom: 2rem;
        }
        
        .empty-state {
          border: 2px dashed #dee2e6;
          border-radius: 0.25rem;
          margin: 2rem 0;
        }
        
        .form-label {
          font-weight: 600;
          margin-bottom: 0.5rem;
        }
      `}</style>
    </div>
  );
};

QuestionnaireBuilder.propTypes = {
  initialQuestionnaire: PropTypes.object,
  isEdit: PropTypes.bool,
  importableFields: PropTypes.object
};

export default QuestionnaireBuilder;